import { Box } from '@sequencehq/core-components'
import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useState
} from 'react'

export const STANDARD_LAYOUT_MODAL_PORTAL_ROOT_ID =
  'standard-layout-modal-portal-root'

const StandardLayoutModalPortalRootContext = createContext<HTMLElement | null>(
  null
)

/**
 * Read the portal-root element for full-screen modals. The value is
 * `null` on the very first render (the ref hasn't been attached yet),
 * then becomes the element via a callback ref before paint, so a
 * consumer can portal into it without a `getElementById` race.
 */
export const useStandardLayoutModalPortalRoot = (): HTMLElement | null =>
  useContext(StandardLayoutModalPortalRootContext)

/**
 * Wrapping container that serves as the portal target for full-screen
 * modals which should cover the navbar+content area but leave the
 * dev-tools sidebar visible (e.g. invoice editor, watchtower manual
 * task processor). Modals look up this element via
 * {@link useStandardLayoutModalPortalRoot} (or `document.getElementById`
 * against {@link STANDARD_LAYOUT_MODAL_PORTAL_ROOT_ID}) and
 * `createPortal` into it.
 *
 * `position: relative` provides the positioning context so portalled
 * modals can use `position: absolute` to fill the available area.
 * The modal itself is responsible for setting a zIndex high enough to
 * sit above the navbar (which has zIndex 2).
 */
export const StandardLayoutModalPortalRoot: FC<PropsWithChildren> = ({
  children
}) => {
  const [target, setTarget] = useState<HTMLElement | null>(null)

  return (
    <Box
      id={STANDARD_LAYOUT_MODAL_PORTAL_ROOT_ID}
      ref={setTarget}
      flex="1"
      minWidth={0}
      height="100%"
      position="relative"
    >
      <StandardLayoutModalPortalRootContext.Provider value={target}>
        {children}
      </StandardLayoutModalPortalRootContext.Provider>
    </Box>
  )
}
